import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  CheckCircle2,
  MapPin,
  Printer,
  RefreshCw,
  RotateCcw,
  Tag,
} from 'lucide-react';
import { api } from '@/lib/api';
import { imprimirEtiquetas } from '@/lib/imprimirEtiquetas';
import { useSessao } from '@/hooks/useSessao';
import { cn, numero } from '@/lib/utils';
import { Botao } from '@/componentes/ui/botao';
import { EstadoErro, EstadoVazio } from '@/componentes/ui/estados';
import { Cartao, CorpoCartao, Esqueleto } from '@/componentes/ui/primitivos';
import { Segmentado } from '@/componentes/ui/segmentado';
import { EtiquetaAlocacaoPapel, type EtiquetaAlocacao } from './EtiquetaAlocacaoPapel';
import { DialogoAlocacao, type EtiquetaParaAlocar } from './DialogoAlocacao';

/**
 * Etiquetas de alocação — o formulário `ETIQUETAS_ALOCACOES` do Procfit.
 *
 * Duas áreas, como no ERP, e a divisão é de trabalho, não de estética:
 *
 *  - **A gerar** — produtos conferidos que ainda não viraram etiqueta. Aqui se
 *    decide a quebra: quantas unidades por volume. É o gesto principal da tela.
 *  - **Geradas** — o que já existe, para reimprimir. Grava em
 *    `_REIMPRESSOES` e pede permissão própria: duas etiquetas com o mesmo
 *    código circulando pelo CD é como o mesmo volume acaba em dois endereços.
 *
 * A QUANTIDADE É DECISÃO DE QUEM RECEBE
 * -------------------------------------
 * O sistema sugere — a última quebra usada para aquele produto —, mas quem
 * está no CD é que sabe que naquele localizador cabem 5 TVs. Por isso o campo
 * vem preenchido e editável, e a tela mostra **antes de gravar** em quantas
 * etiquetas aquilo vai dar e quanto sobra na última. Gerar 67 etiquetas
 * erradas custa uma bobina e uma hora.
 *
 * O AVISO DE NÃO APROVADO NÃO É DECORAÇÃO
 * ---------------------------------------
 * `Usp_ValidarEtiquetaAlocacao` recusa a leitura com "Recebimento Não Está
 * Aprovado, Alocação Não Permitida". Sem o aviso aqui, alguém imprime 60
 * etiquetas, sobe com elas e descobre no coletor.
 */

type Area = 'gerar' | 'geradas';
/** Sub-aba de "Geradas": o estado de cada etiqueta no chão. */
type Estado = 'todas' | 'guardar' | 'alocadas';

interface ProdutoParaGerar {
  produto: number;
  descricao: string | null;
  ean: string | null;
  conferida: number;
  sugestao: number | null;
}

interface LoteEtiquetas {
  recebimento: number;
  aprovado: boolean;
  aGerar: ProdutoParaGerar[];
  etiquetas: EtiquetaAlocacao[];
  total: number;
  naoAlocadas: number;
}

export interface CabecalhoRecebimento {
  recebimento: number;
  data: string | null;
  fornecedor: string | null;
  fornecedorCodigo: number | null;
  notaFiscal: number | null;
  serie: string | null;
}

/** Como a carga se divide — o que a tela mostra antes de gravar. */
function quebra(conferida: number, porEtiqueta: number): {
  quantas: number;
  cheias: number;
  resto: number;
} | null {
  if (!Number.isFinite(porEtiqueta) || porEtiqueta <= 0 || porEtiqueta > conferida) return null;
  const quantas = Math.ceil(conferida / porEtiqueta);
  const resto = conferida - (quantas - 1) * porEtiqueta;
  return { quantas, cheias: quantas - 1, resto };
}

function dataBr(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
}

export function PainelEtiquetasAlocacao({
  recebimento,
  cabecalho,
  empresa,
  centro,
  aoAbrir,
}: {
  recebimento: number | null;
  cabecalho: CabecalhoRecebimento | null;
  empresa: number | null;
  centro: number | null;
  aoAbrir: (recebimento: number) => void;
}): JSX.Element {
  const cliente = useQueryClient();
  const { temPermissao } = useSessao();
  const podeGerar = temPermissao('recebimento.etiqueta');
  const podeAlocar = temPermissao('recebimento.alocar');
  const [alocando, definirAlocando] = React.useState<EtiquetaParaAlocar | null>(null);

  const [area, definirArea] = React.useState<Area>('gerar');
  const [estado, definirEstado] = React.useState<Estado>('todas');
  const [marcadas, definirMarcadas] = React.useState<Set<number>>(new Set());
  const [quantidades, definirQuantidades] = React.useState<Record<number, string>>({});
  const [erro, definirErro] = React.useState<string | null>(null);
  const [aviso, definirAviso] = React.useState<string | null>(null);
  const papel = React.useRef<HTMLDivElement>(null);
  const [paraImprimir, definirParaImprimir] = React.useState<EtiquetaAlocacao[]>([]);

  const consulta = useQuery<LoteEtiquetas>({
    queryKey: ['etiquetas-alocacao', recebimento],
    queryFn: () => api.get<LoteEtiquetas>(`/api/recebimentos/${recebimento}/etiquetas-alocacao`),
    enabled: recebimento !== null,
    staleTime: 30_000,
  });

  const dados = consulta.data;
  const etiquetas = React.useMemo(() => dados?.etiquetas ?? [], [dados]);
  const aGerar = React.useMemo(() => dados?.aGerar ?? [], [dados]);

  const totalPorProduto = React.useMemo(() => {
    const mapa = new Map<number, number>();
    for (const e of etiquetas) mapa.set(e.produto, (mapa.get(e.produto) ?? 0) + 1);
    return mapa;
  }, [etiquetas]);

  // A sugestão do servidor preenche o campo; a digitação do usuário manda.
  React.useEffect(() => {
    definirQuantidades((antes) => {
      const novo = { ...antes };
      for (const p of aGerar) {
        if (novo[p.produto] === undefined) {
          novo[p.produto] = String(p.sugestao ?? p.conferida);
        }
      }
      return novo;
    });
  }, [aGerar]);

  React.useEffect(() => {
    definirMarcadas(new Set());
    definirErro(null);
    definirAviso(null);
    definirQuantidades({});
  }, [recebimento]);

  // Manter marcada uma etiqueta que saiu da lista imprimiria o que ninguém
  // está vendo — trocar de sub-aba zera a seleção.
  React.useEffect(() => definirMarcadas(new Set()), [estado, area]);

  // Sai da aba "A gerar" sozinho quando não há mais nada a gerar — do
  // contrário a tela abre num painel vazio depois de um trabalho bem-feito.
  React.useEffect(() => {
    if (dados && aGerar.length === 0 && etiquetas.length > 0) definirArea('geradas');
  }, [dados, aGerar.length, etiquetas.length]);

  const imprimir = React.useCallback((lista: EtiquetaAlocacao[]): void => {
    if (lista.length === 0) return;
    definirParaImprimir(lista);
    // O papel só existe depois do render: imprimir no mesmo tique pegaria a
    // lista anterior, que é como sairiam as etiquetas erradas.
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        if (papel.current) imprimirEtiquetas(papel.current);
      });
    });
  }, []);

  const gerar = useMutation({
    mutationFn: (itens: { produto: number; porEtiqueta: number }[]) =>
      api.post<{ lote: number; geradas: number }>(
        `/api/recebimentos/${recebimento}/etiquetas-alocacao/gerar`,
        { itens },
      ),
    onSuccess: async (feita) => {
      definirErro(null);
      definirAviso(
        `Lote ${feita.lote} criado com ${feita.geradas} ${feita.geradas === 1 ? 'etiqueta' : 'etiquetas'}. Imprima na aba Geradas.`,
      );
      await cliente.invalidateQueries({ queryKey: ['etiquetas-alocacao', recebimento] });
    },
    onError: (e) => definirErro(e instanceof Error ? e.message : 'Falha ao gerar.'),
  });

  const reimpressao = useMutation({
    mutationFn: (ids: number[]) =>
      api.post<{ registradas: number }>(
        `/api/recebimentos/${recebimento}/etiquetas-alocacao/reimpressao`,
        { etiquetas: ids },
      ),
    onSuccess: async () => {
      await cliente.invalidateQueries({ queryKey: ['etiquetas-alocacao', recebimento] });
    },
    onError: (e) => definirErro(e instanceof Error ? e.message : 'Falha ao registrar.'),
  });

  if (recebimento === null) {
    return <FilaSemEtiqueta centro={centro} aoAbrir={aoAbrir} />;
  }

  const alocadas = etiquetas.filter((e) => e.alocada);
  const aGuardar = etiquetas.filter((e) => !e.alocada);
  const visiveis =
    estado === 'alocadas' ? alocadas : estado === 'guardar' ? aGuardar : etiquetas;

  const selecionadas = visiveis.filter((e) => marcadas.has(e.id));
  const itensValidos = aGerar
    .map((p) => ({ p, q: quebra(p.conferida, Number(quantidades[p.produto])) }))
    .filter((x) => x.q !== null);

  return (
    <div className="space-y-3">
      {/*
        O RECEBIMENTO SELECIONADO, SEMPRE VISÍVEL.

        Antes ele só existia na barra de endereço: quem abria a aba via uma
        lista de etiquetas sem saber de qual carga eram. Numa tela que imprime
        e grava no ERP, isso é o primeiro dado, não o último.
      */}
      <Cartao>
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 px-4 py-3">
          <div>
            <p className="rotulo">Recebimento</p>
            <p className="tabular font-mono text-[22px] font-bold leading-tight">{recebimento}</p>
          </div>
          <div className="min-w-0 flex-1">
            <p className="rotulo">Fornecedor</p>
            <p className="truncate text-[13px] font-semibold" title={cabecalho?.fornecedor ?? ''}>
              {cabecalho?.fornecedorCodigo ? `${cabecalho.fornecedorCodigo} · ` : ''}
              {cabecalho?.fornecedor ?? '—'}
            </p>
          </div>
          <div>
            <p className="rotulo">Nota fiscal</p>
            <p className="tabular font-mono text-[13px] font-semibold">
              {cabecalho?.notaFiscal ?? '—'}
              {cabecalho?.serie ? `/${cabecalho.serie}` : ''}
            </p>
          </div>
          <div>
            <p className="rotulo">Recebido em</p>
            <p className="tabular text-[13px] font-semibold">{dataBr(cabecalho?.data ?? null)}</p>
          </div>
          {dados ? (
            <div>
              <p className="rotulo">Situação</p>
              <p
                className={cn(
                  'text-[13px] font-bold',
                  dados.aprovado ? 'text-sucesso' : 'text-atencao',
                )}
              >
                {dados.aprovado ? 'Aprovado' : 'Não aprovado'}
              </p>
            </div>
          ) : null}
        </div>
      </Cartao>

      {erro ? (
        <div className="flex items-start gap-2.5 rounded-xl border border-perigo/40 bg-perigo/10 px-3 py-2.5 text-[13px]">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-perigo" />
          <span>{erro}</span>
        </div>
      ) : null}

      {aviso ? (
        <div className="flex items-start gap-2.5 rounded-xl border border-sucesso/40 bg-sucesso/10 px-3 py-2.5 text-[13px] text-sucesso">
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
          <span className="font-semibold">{aviso}</span>
        </div>
      ) : null}

      {dados && !dados.aprovado && (dados.total > 0 || aGerar.length > 0) ? (
        <div className="flex items-start gap-2.5 rounded-xl border border-atencao/40 bg-atencao/10 px-3 py-2.5 text-[13px]">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-atencao" />
          <p>
            <strong>Recebimento não aprovado.</strong> O coletor vai recusar estas etiquetas com
            &ldquo;Alocação Não Permitida&rdquo;. Aprove na aba <strong>Aprovar</strong> antes de
            subir com elas.
          </p>
        </div>
      ) : null}

      <Cartao className="relative flex min-h-0 flex-col">
        <div className="flex flex-wrap items-center gap-2 border-b border-borda px-4 py-2.5">
          <Segmentado
            tamanho="pequeno"
            valor={area}
            aoMudar={definirArea}
            rotuloAcessivel="Área das etiquetas"
            opcoes={[
              { valor: 'gerar', rotulo: 'A gerar', contagem: aGerar.length },
              { valor: 'geradas', rotulo: 'Geradas', contagem: dados?.total ?? 0 },
            ]}
          />
          {dados && dados.total > 0 ? (
            <span className="tabular text-[11.5px] text-textoFraco">
              {numero(dados.naoAlocadas)} ainda não bipadas pelo coletor
            </span>
          ) : null}

          <div className="ml-auto flex items-center gap-2">
            <Botao
              variante="fantasma"
              tamanho="pequeno"
              onClick={() => void consulta.refetch()}
              carregando={consulta.isFetching}
            >
              <RefreshCw className="h-4 w-4" />
              Atualizar
            </Botao>

            {area === 'gerar' ? (
              <Botao
                variante="primaria"
                tamanho="pequeno"
                disabled={!podeGerar || itensValidos.length === 0}
                carregando={gerar.isPending}
                onClick={() =>
                  gerar.mutate(
                    itensValidos.map((x) => ({
                      produto: x.p.produto,
                      porEtiqueta: Number(quantidades[x.p.produto]),
                    })),
                  )
                }
              >
                <Tag className="h-4 w-4" />
                Gerar {numero(itensValidos.reduce((s, x) => s + (x.q?.quantas ?? 0), 0))} etiquetas
              </Botao>
            ) : (
              <>
                <Botao
                  variante="primaria"
                  tamanho="pequeno"
                  disabled={visiveis.length === 0}
                  onClick={() => imprimir(selecionadas.length > 0 ? selecionadas : visiveis)}
                >
                  <Printer className="h-4 w-4" />
                  {selecionadas.length > 0
                    ? `Imprimir ${selecionadas.length}`
                    : `Imprimir ${visiveis.length}`}
                </Botao>
                <Botao
                  variante="secundaria"
                  tamanho="pequeno"
                  disabled={!podeGerar || selecionadas.length === 0}
                  carregando={reimpressao.isPending}
                  onClick={() => {
                    reimpressao.mutate(selecionadas.map((e) => e.id));
                    imprimir(selecionadas);
                  }}
                >
                  <RotateCcw className="h-4 w-4" />
                  Reimprimir {selecionadas.length}
                </Botao>
              </>
            )}
          </div>
        </div>

        {!podeGerar ? (
          <div className="border-b border-borda bg-superficie2 px-4 py-2 text-[12px] text-textoFraco">
            Você pode ver as etiquetas, mas não gerar nem reimprimir. A permissão{' '}
            <strong>Reimprimir etiqueta de alocação</strong> é concedida em Administração &gt;
            Usuários.
          </div>
        ) : null}

        <CorpoCartao className="min-h-0 flex-1 overflow-y-auto">
          {consulta.isLoading ? (
            <Esqueleto className="h-64" />
          ) : consulta.isError ? (
            <EstadoErro erro={consulta.error} aoTentarDeNovo={() => void consulta.refetch()} />
          ) : area === 'gerar' ? (
            aGerar.length === 0 ? (
              <EstadoVazio
                icone={<CheckCircle2 className="h-7 w-7" />}
                titulo="Nada a gerar"
                detalhe={
                  etiquetas.length > 0
                    ? 'Todos os produtos conferidos deste recebimento já têm etiqueta.'
                    : 'Este recebimento ainda não tem produtos conferidos pelo coletor.'
                }
              />
            ) : (
              <TabelaGerar
                produtos={aGerar}
                quantidades={quantidades}
                aoMudar={(produto, valor) =>
                  definirQuantidades((antes) => ({ ...antes, [produto]: valor }))
                }
              />
            )
          ) : etiquetas.length === 0 ? (
            <EstadoVazio
              icone={<Tag className="h-7 w-7" />}
              titulo="Sem etiquetas geradas"
              detalhe="Gere as etiquetas na aba “A gerar”."
            />
          ) : (
            <div className="space-y-2">
              {/*
                A sub-aba responde a pergunta do supervisor no fim do dia: o que
                ainda não subiu para a prateleira. "A guardar" é trabalho que
                falta; "Alocadas" é conferência do que já foi.
              */}
              <div className="flex flex-wrap items-center gap-2">
                <Segmentado
                  tamanho="pequeno"
                  valor={estado}
                  aoMudar={definirEstado}
                  rotuloAcessivel="Estado das etiquetas"
                  opcoes={[
                    { valor: 'todas', rotulo: 'Todas', contagem: etiquetas.length },
                    { valor: 'guardar', rotulo: 'A guardar', contagem: aGuardar.length },
                    { valor: 'alocadas', rotulo: 'Alocadas', contagem: alocadas.length },
                  ]}
                />
                {estado === 'guardar' && aGuardar.length === 0 ? (
                  <span className="text-[12px] font-semibold text-sucesso">
                    Tudo guardado — nenhuma etiqueta pendente no chão.
                  </span>
                ) : null}
              </div>

              {visiveis.length === 0 ? (
                <EstadoVazio
                  icone={<CheckCircle2 className="h-7 w-7" />}
                  titulo={
                    estado === 'guardar' ? 'Nada a guardar' : 'Nenhuma etiqueta alocada ainda'
                  }
                  detalhe={
                    estado === 'guardar'
                      ? 'Todas as etiquetas deste recebimento já foram bipadas pelo coletor.'
                      : 'O coletor ainda não bipou nenhuma etiqueta deste recebimento.'
                  }
                />
              ) : (
                <TabelaGeradas
                  mostrarDestino={estado !== 'guardar'}
                  aoAlocar={podeAlocar ? (e) => definirAlocando({
                    codigoBarras: e.codigoBarras,
                    recebimento: e.recebimento,
                    produto: e.produto,
                    descricao: e.descricao,
                    quantidade: e.quantidade,
                    sequencia: e.sequencia,
                  }) : null}
                  etiquetas={visiveis}
                  marcadas={marcadas}
                  aoMarcar={(id) =>
                    definirMarcadas((antes) => {
                      const novo = new Set(antes);
                      if (novo.has(id)) novo.delete(id);
                      else novo.add(id);
                      return novo;
                    })
                  }
                  aoMarcarTodas={() =>
                    definirMarcadas((antes) =>
                      antes.size === visiveis.length
                        ? new Set()
                        : new Set(visiveis.map((e) => e.id)),
                    )
                  }
                  aoImprimirUma={(e) => imprimir([e])}
                />
              )}
            </div>
          )}
        </CorpoCartao>
      </Cartao>

      <DialogoAlocacao
        etiqueta={alocando}
        empresa={empresa}
        centro={centro}
        aoFechar={() => definirAlocando(null)}
        aoConcluir={() => void consulta.refetch()}
      />

      {/* O papel fica montado fora da tela — é ele que a impressão copia. */}
      <div className="pointer-events-none h-0 overflow-hidden" aria-hidden>
        <div ref={papel} className="absolute left-[-10000px] top-0">
          {paraImprimir.map((e) => (
            <EtiquetaAlocacaoPapel key={e.id} etiqueta={e} total={totalPorProduto.get(e.produto)} />
          ))}
        </div>
      </div>
    </div>
  );
}

function TabelaGerar({
  produtos,
  quantidades,
  aoMudar,
}: {
  produtos: ProdutoParaGerar[];
  quantidades: Record<number, string>;
  aoMudar: (produto: number, valor: string) => void;
}): JSX.Element {
  return (
    <table className="w-full text-left">
      <thead>
        <tr className="border-b border-borda text-textoFraco">
          <th className="rotulo pb-1.5 pr-2">Produto</th>
          <th className="rotulo pb-1.5 pr-2">Descrição</th>
          <th className="rotulo pb-1.5 pr-2 text-right">Conferido</th>
          <th className="rotulo pb-1.5 pr-2">Un. por etiqueta</th>
          <th className="rotulo pb-1.5">Vai gerar</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-borda">
        {produtos.map((p) => {
          const bruto = quantidades[p.produto] ?? '';
          const q = quebra(p.conferida, Number(bruto));
          return (
            <tr key={p.produto}>
              <td className="tabular py-2 pr-2 font-mono text-[13px] font-bold">{p.produto}</td>
              <td
                className="max-w-[280px] truncate py-2 pr-2 text-[12.5px]"
                title={p.descricao ?? ''}
              >
                {p.descricao ?? '—'}
              </td>
              <td className="tabular py-2 pr-2 text-right text-[13px] font-bold">
                {numero(p.conferida)}
              </td>
              <td className="py-2 pr-2">
                <input
                  value={bruto}
                  onChange={(e) => aoMudar(p.produto, e.target.value.replace(/[^\d]/g, ''))}
                  inputMode="numeric"
                  aria-label={`Unidades por etiqueta do produto ${p.produto}`}
                  className={cn(
                    'tabular min-h-[34px] w-[86px] rounded-lg border bg-superficie px-2 text-center font-mono text-[15px] font-bold',
                    q ? 'border-borda' : 'border-perigo/60',
                  )}
                />
              </td>
              <td className="py-2 text-[12px]">
                {q ? (
                  <span>
                    <strong className="tabular text-[14px]">{numero(q.quantas)}</strong> etiquetas
                    <span className="text-textoFraco">
                      {' '}
                      — {numero(q.cheias)} × {numero(Number(bruto))}
                      {/* A última leva o resto: é o que fecha a carga exata. */}
                      {q.resto !== Number(bruto) ? ` + 1 × ${numero(q.resto)}` : ` + 1 × ${numero(q.resto)}`}
                    </span>
                  </span>
                ) : (
                  <span className="text-perigo">
                    {bruto === '' ? 'informe a quantidade' : 'não pode passar do conferido'}
                  </span>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function TabelaGeradas({
  etiquetas,
  marcadas,
  aoMarcar,
  aoMarcarTodas,
  aoImprimirUma,
  mostrarDestino,
  aoAlocar,
}: {
  etiquetas: EtiquetaAlocacao[];
  marcadas: Set<number>;
  aoMarcar: (id: number) => void;
  aoMarcarTodas: () => void;
  aoImprimirUma: (e: EtiquetaAlocacao) => void;
  /** Mostra onde/quando/por quem foi guardada. Só faz sentido fora de "a guardar". */
  mostrarDestino: boolean;
  /** `null` quando o usuário não tem permissão de alocar. */
  aoAlocar: ((e: EtiquetaAlocacao) => void) | null;
}): JSX.Element {
  const todas = marcadas.size === etiquetas.length && etiquetas.length > 0;
  return (
    <table className="w-full text-left">
      <thead>
        <tr className="border-b border-borda text-textoFraco">
          <th className="w-8 pb-1.5">
            <input
              type="checkbox"
              checked={todas}
              onChange={aoMarcarTodas}
              aria-label="Marcar todas as etiquetas"
              className="h-3.5 w-3.5 align-middle"
            />
          </th>
          <th className="rotulo pb-1.5 pr-2">Seq.</th>
          <th className="rotulo pb-1.5 pr-2">Produto</th>
          <th className="rotulo pb-1.5 pr-2">Descrição</th>
          <th className="rotulo pb-1.5 pr-2 text-right">Qtde</th>
          <th className="rotulo pb-1.5 pr-2">Código de barras</th>
          <th className="rotulo pb-1.5 pr-2">Estado</th>
          {mostrarDestino ? <th className="rotulo pb-1.5 pr-2">Guardada em</th> : null}
          <th className="pb-1.5" />
        </tr>
      </thead>
      <tbody className="divide-y divide-borda">
        {etiquetas.map((e) => (
          <tr key={e.id} className={cn(marcadas.has(e.id) && 'bg-primaria-suave/40')}>
            <td className="py-1.5">
              <input
                type="checkbox"
                checked={marcadas.has(e.id)}
                onChange={() => aoMarcar(e.id)}
                aria-label={`Marcar etiqueta ${e.codigoBarras}`}
                className="h-3.5 w-3.5 align-middle"
              />
            </td>
            <td className="tabular py-1.5 pr-2 font-mono text-[12px] font-bold">{e.sequencia}</td>
            <td className="tabular py-1.5 pr-2 font-mono text-[12px]">{e.produto}</td>
            <td
              className="max-w-[260px] truncate py-1.5 pr-2 text-[12px]"
              title={e.descricao ?? ''}
            >
              {e.descricao ?? '—'}
            </td>
            <td className="tabular py-1.5 pr-2 text-right text-[12px] font-bold">{e.quantidade}</td>
            <td className="tabular py-1.5 pr-2 font-mono text-[11.5px] text-textoFraco">
              {e.codigoBarras}
            </td>
            <td className="py-1.5 pr-2">
              {e.alocada ? (
                <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-sucesso">
                  <CheckCircle2 className="h-3 w-3" />
                  alocada
                </span>
              ) : (
                <span className="text-[11px] font-semibold text-atencao">a guardar</span>
              )}
              {e.reimpressoes > 0 ? (
                <span className="tabular ml-1.5 text-[10.5px] text-textoFraco">
                  · {e.reimpressoes + 1}ª via
                </span>
              ) : null}
            </td>
            {mostrarDestino ? (
              <td className="py-1.5 pr-2 text-[11.5px]">
                {e.alocada ? (
                  <span>
                    <span className="tabular font-mono font-bold">{e.endereco ?? '—'}</span>
                    <span className="text-textoFraco">
                      {' '}
                      · {dataBr(e.alocadaEm)}
                      {e.alocadaPor ? ` · crachá ${e.alocadaPor}` : ''}
                    </span>
                  </span>
                ) : (
                  <span className="text-textoFraco">—</span>
                )}
              </td>
            ) : null}
            <td className="whitespace-nowrap py-1.5 text-right">
              {/* Alocar direto da linha: quem vê "a guardar" é quem vai guardar. */}
              {aoAlocar && !e.alocada ? (
                <Botao variante="secundaria" tamanho="pequeno" onClick={() => aoAlocar(e)}>
                  <MapPin className="h-3.5 w-3.5" />
                  Alocar
                </Botao>
              ) : null}
              <Botao
                variante="fantasma"
                tamanho="pequeno"
                className="ml-1"
                onClick={() => aoImprimirUma(e)}
              >
                <Printer className="h-3.5 w-3.5" />
              </Botao>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

interface RecebimentoSemEtiqueta {
  recebimento: number;
  fornecedor: string | null;
  notaFiscal: number | null;
  aprovadoEm: string | null;
  produtos: number;
  semEtiqueta: number;
}

/**
 * Ponto de partida da aba: aprovados da empresa com produto ainda sem etiqueta.
 *
 * Pedido da operação (27/10/2026). O campo "Recebimento nº" continua em cima
 * para quem já está com o papel na mão; a lista é para quem abre a aba sem saber
 * o que está parado. Mais antigo primeiro, porque é o que está esquecido. A
 * busca filtra por número, fornecedor ou NF.
 */
function FilaSemEtiqueta({
  centro,
  aoAbrir,
}: {
  centro: number | null;
  aoAbrir: (recebimento: number) => void;
}): JSX.Element {
  const [busca, definirBusca] = React.useState('');
  const fila = useQuery<{ centro: number; recebimentos: RecebimentoSemEtiqueta[] }>({
    queryKey: ['etiquetas-alocacao-pendentes', centro],
    queryFn: () =>
      api.get<{ centro: number; recebimentos: RecebimentoSemEtiqueta[] }>(
        '/api/recebimentos/etiquetas-alocacao/pendentes',
        { centro },
      ),
    enabled: centro !== null,
    staleTime: 30_000,
  });

  const todos = fila.data?.recebimentos ?? [];
  const termo = busca.trim().toLowerCase();
  const visiveis = termo
    ? todos.filter(
        (r) =>
          String(r.recebimento).includes(termo) ||
          (r.notaFiscal !== null && String(r.notaFiscal).includes(termo)) ||
          (r.fornecedor ?? '').toLowerCase().includes(termo),
      )
    : todos;

  return (
    <Cartao className="flex min-h-0 flex-col">
      <div className="flex flex-wrap items-center gap-2 border-b border-borda px-4 py-2.5">
        <span className="text-[13px] font-bold">Aguardando etiqueta</span>
        <span className="tabular text-[11.5px] text-textoFraco">
          {fila.isLoading ? '' : `${todos.length} aprovado(s) nesta empresa`}
        </span>
        <input
          value={busca}
          onChange={(e) => definirBusca(e.target.value)}
          placeholder="Buscar nº, fornecedor ou NF"
          aria-label="Buscar recebimento"
          className="ml-auto h-8 w-60 rounded-lg border border-borda bg-superficie px-2.5 text-[12.5px] outline-none focus:border-primaria"
        />
        <Botao
          variante="fantasma"
          tamanho="pequeno"
          onClick={() => void fila.refetch()}
          carregando={fila.isFetching}
        >
          <RefreshCw className="h-4 w-4" />
          Atualizar
        </Botao>
      </div>
      <CorpoCartao className="min-h-0 flex-1 overflow-y-auto">
        {fila.isLoading ? (
          <Esqueleto className="h-48" />
        ) : fila.isError ? (
          <EstadoErro erro={fila.error} aoTentarDeNovo={() => void fila.refetch()} />
        ) : todos.length === 0 ? (
          <EstadoVazio
            icone={<Tag className="h-7 w-7" />}
            titulo="Nada aguardando etiqueta"
            detalhe="Todo recebimento aprovado nos últimos 60 dias desta empresa já tem etiqueta. Para reimprimir, digite o número no campo “Recebimento nº”, acima."
          />
        ) : visiveis.length === 0 ? (
          <EstadoVazio titulo="Nenhum resultado" detalhe={`Nada na fila combina com “${busca}”.`} />
        ) : (
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-borda text-textoFraco">
                <th className="rotulo pb-1.5 pr-2">Recebimento</th>
                <th className="rotulo pb-1.5 pr-2">Aprovado em</th>
                <th className="rotulo pb-1.5 pr-2">Fornecedor</th>
                <th className="rotulo pb-1.5 pr-2">NF</th>
                <th className="rotulo pb-1.5 pr-2 text-right">Sem etiqueta</th>
                <th className="pb-1.5" />
              </tr>
            </thead>
            <tbody className="divide-y divide-borda">
              {visiveis.map((r) => (
                <tr
                  key={r.recebimento}
                  className="cursor-pointer hover:bg-superficie2"
                  onClick={() => aoAbrir(r.recebimento)}
                >
                  <td className="tabular py-2 pr-2 font-mono text-[14px] font-bold">
                    {r.recebimento}
                  </td>
                  <td className="tabular py-2 pr-2 text-[12px] text-textoFraco">
                    {dataBr(r.aprovadoEm)}
                  </td>
                  <td
                    className="max-w-[260px] truncate py-2 pr-2 text-[12.5px]"
                    title={r.fornecedor ?? ''}
                  >
                    {r.fornecedor ?? '—'}
                  </td>
                  <td className="tabular py-2 pr-2 font-mono text-[12px]">{r.notaFiscal ?? '—'}</td>
                  <td className="tabular py-2 pr-2 text-right text-[12px]">
                    <strong>{r.semEtiqueta}</strong>
                    <span className="text-textoFraco"> de {r.produtos} produto(s)</span>
                  </td>
                  <td className="py-2 text-right">
                    <Botao variante="secundaria" tamanho="pequeno">
                      <Tag className="h-4 w-4" />
                      Abrir
                    </Botao>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CorpoCartao>
    </Cartao>
  );
}
